import { FC, FormEvent, useEffect, useId, useRef, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { privacyPolicyUrl } from 'src/utils/siteLinks';
import {
  FREE_MONTH_CONSENT_VERSION_FE,
  FreeMonthRequestOutcome,
  fetchFreeMonthStatus,
  submitFreeMonthRequest,
} from 'src/app/services/freeMonthSignup';
import { TurnstileAbortError, TurnstileLoadError, requestTurnstileToken } from 'src/components/organisms/billing/turnstileToken';
import { MESSAGE_KEY, renderFreeMonthMessage } from 'src/pages/freeMonthMessages';
import {
  FREE_MONTH_MAX,
  FreeMonthLocale,
  buildFreeMonthHandoffUrl,
  looksLikeEmail,
} from 'src/utils/freeMonth';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Validated PROD /start URL (see freeMonthStartUrl). */
  startUrl: string;
  locale: FreeMonthLocale;
  /** PROD API origin the request goes to (freeMonthApiOrigin); default: origin of startUrl. */
  apiOrigin?: string;
};

/**
 * Free-month pop-up (FM-16). The three fields live in component state only
 * and are dropped on close or after a successful request. "Continue" submits
 * the request itself to the PROD API (public free-month routes, invisible
 * Turnstile token per attempt) and shows the "check your email" state here;
 * the demo session stays open. Only when the API or the Turnstile script is
 * unavailable (not a server answer) it falls back to the old hand-over:
 * this tab goes to PROD /start with the fields in the URL fragment.
 * The fields and the token are never logged, stored or put in a query string.
 */
export const DemoFreeMonthDialog: FC<Props> = ({ open, onClose, startUrl, locale, apiOrigin }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const bodyId = `${baseId}-body`;
  const errorId = `${baseId}-error`;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<FreeMonthRequestOutcome | null>(null);
  const submittingRef = useRef(false);
  const generationRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const turnstileBoxRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<{ enabled: boolean; turnstileSiteKey?: string } | null>(null);

  useEffect(() => {
    if (open) return;
    generationRef.current += 1; // a request still in flight must not touch the closed dialog
    abortRef.current?.abort(); // stops a token still being minted; its widget is removed
    abortRef.current = null;
    submittingRef.current = false;
    statusRef.current = null;
    setName('');
    setEmail('');
    setOrganisation('');
    setInvalid(false);
    setSubmitting(false);
    setOutcome(null);
  }, [open]);

  const complete = !!name.trim() && !!email.trim() && !!organisation.trim();
  const origin = apiOrigin ?? new URL(startUrl).origin;

  const handOver = () =>
    window.location.assign(buildFreeMonthHandoffUrl(startUrl, { name, email, organisation }, locale));

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!complete || submittingRef.current) return;
    if (!looksLikeEmail(email)) {
      setInvalid(true);
      return;
    }
    submittingRef.current = true;
    const generation = generationRef.current;
    const current = () => generation === generationRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    setSubmitting(true);
    setOutcome(null);
    const finish = (result: FreeMonthRequestOutcome | 'fallback') => {
      if (!current()) return;
      submittingRef.current = false;
      setSubmitting(false);
      if (result === 'fallback') {
        handOver();
        return;
      }
      setOutcome(result);
      if (result === 'check_email') {
        setName('');
        setEmail('');
        setOrganisation('');
      }
    };
    const status = statusRef.current ?? (await fetchFreeMonthStatus(origin));
    if (!current()) return; // closed (or re-opened) while the status was fetched: nothing is sent
    statusRef.current = status;
    if (!status.enabled || !status.turnstileSiteKey || !turnstileBoxRef.current) {
      statusRef.current = null;
      finish('fallback');
      return;
    }
    let token: string;
    try {
      token = await requestTurnstileToken({
        siteKey: status.turnstileSiteKey,
        action: 'free_month',
        locale,
        container: turnstileBoxRef.current,
        signal: controller.signal,
      });
    } catch (error) {
      if (error instanceof TurnstileAbortError || !current()) return;
      finish(error instanceof TurnstileLoadError ? 'fallback' : 'turnstile');
      return;
    }
    if (!current()) return; // closed (or re-opened) while the token was minted: no request, no mail
    const result = await submitFreeMonthRequest(
      {
        name: name.trim(),
        email: email.trim(),
        organisation: organisation.trim(),
        locale,
        consentVersion: FREE_MONTH_CONSENT_VERSION_FE,
        turnstileToken: token,
      },
      origin
    );
    finish(result);
  };

  const done = outcome === 'check_email';
  const messageKey = outcome ? MESSAGE_KEY[outcome] : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      fullScreen={fullScreen}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
    >
      <DialogTitle id={titleId} sx={{ pr: 7 }}>
        {t('demo.freeMonth.title')}
        <IconButton
          aria-label={t('close') as string}
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {done ? (
        <>
          <DialogContent>
            <Alert severity="success" role="status" data-testid="free-month-success">
              {t('startFreeMonth.checkEmail')}
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={onClose} data-testid="free-month-close">
              {t('close')}
            </Button>
          </DialogActions>
        </>
      ) : (
      <Box component="form" onSubmit={onSubmit} noValidate data-testid="free-month-form">
        <DialogContent>
          <Typography id={bodyId} variant="body2" sx={{ mb: 2 }}>
            {t('demo.freeMonth.body')}
          </Typography>
          <TextField
            label={t('demo.freeMonth.nameLabel')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            required
            fullWidth
            margin="dense"
            inputProps={{ maxLength: FREE_MONTH_MAX.name, 'data-testid': 'free-month-name' }}
          />
          <TextField
            label={t('demo.freeMonth.emailLabel')}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setInvalid(false);
            }}
            autoComplete="email"
            required
            fullWidth
            margin="dense"
            error={invalid}
            inputProps={{
              maxLength: FREE_MONTH_MAX.email,
              'data-testid': 'free-month-email',
              'aria-describedby': invalid ? errorId : undefined,
            }}
          />
          <TextField
            label={t('demo.freeMonth.orgLabel')}
            value={organisation}
            onChange={(e) => setOrganisation(e.target.value)}
            autoComplete="organization"
            required
            fullWidth
            margin="dense"
            inputProps={{
              maxLength: FREE_MONTH_MAX.organisation,
              'data-testid': 'free-month-org',
            }}
          />
          {invalid && (
            <Typography
              id={errorId}
              role="alert"
              variant="body2"
              color="error"
              data-testid="free-month-error"
              sx={{ mt: 1 }}
            >
              {t('demo.lead.errorInvalid')}
            </Typography>
          )}
          {messageKey && (
            <Alert severity="error" role="alert" data-testid="free-month-submit-error" sx={{ mt: 1 }}>
              {renderFreeMonthMessage(messageKey, t(messageKey))}
            </Alert>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            component="p"
            data-testid="free-month-consent"
            sx={{ mt: 2 }}
          >
            {t('demo.freeMonth.consent')}
          </Typography>
          <Link
            href={privacyPolicyUrl(i18n.language)}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
          >
            {t('demo.freeMonth.privacyLink')}
          </Link>
          <div ref={turnstileBoxRef} data-testid="free-month-turnstile" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} data-testid="free-month-close">
            {t('close')}
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            disabled={!complete}
            loading={submitting}
            data-testid="free-month-continue"
            sx={{ minHeight: 44 }}
          >
            {t('demo.freeMonth.continue')}
          </LoadingButton>
        </DialogActions>
      </Box>
      )}
    </Dialog>
  );
};
